import { useState, useEffect } from 'react';
import { Save, Loader2, MessageSquare, MessageSquareMore, UserX, UserPlus, Globe, Image as ImageIcon, Rss, Code, ShieldAlert, History, Route, Hammer, ArrowUpLeft, ArrowUp, ArrowUpRight, ArrowLeft, ArrowRight, ArrowDownLeft, ArrowDown, ArrowDownRight, Moon, Sun, RotateCcw, Lock, Users, Database } from 'lucide-react';
import { Site } from '../types';
import { callSiteApi } from '../utils';
import { toast } from '@/hooks/use-toast';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  getFeedbackToken,
  activateFeedback,
  deactivateFeedback,
  updateFeedbackSettings,
  type FeedbackToken,
} from '@/lib/feedbackService';
import { useWorkspace } from '@/context/WorkspaceContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HexColorPicker } from 'react-colorful';

interface SiteSettingsTabProps {
  site: Site;
}

export function SiteSettingsTab({ site }: SiteSettingsTabProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Feedback widget state
  const [feedbackToken, setFeedbackToken] = useState<FeedbackToken | null>(null);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
  const [localAllowGuests, setLocalAllowGuests] = useState(true);
  const [localWidgetPosition, setLocalWidgetPosition] = useState('bottom-right');
  const [localWidgetTheme, setLocalWidgetTheme] = useState('dark');
  
  const { currentWorkspace } = useWorkspace();
  const defaultWorkspaceAccent = currentWorkspace?.appearance?.theme?.accentColor || '#3B82F6';
  const [localWidgetAccentColor, setLocalWidgetAccentColor] = useState('');
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  
  const [settings, setSettings] = useState({
    disable_comments: false,
    disable_new_user_registration: false,
    search_engine_visibility: false,
    svg_upload_enabled: false,
    disable_feeds: false,
    disable_embeds: false,
    disable_xmlrpc: false,
    revisions_limit: -1,
    redirect_404_to_homepage: false,
    maintenance_mode: false,
    coming_soon_mode: false,
    coming_soon_date: '',
    login_branding_enabled: false,
    custom_login_url: '',
    form_submissions_enabled: false,
    form_submissions_plugin: 'fluent_forms',
  });

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const data = await callSiteApi(site.url, site.publicKey, site.secretKey, 'settings');
      if (data) {
        setSettings({
          disable_comments: data.disable_comments || false,
          disable_new_user_registration: data.disable_new_user_registration || false,
          search_engine_visibility: data.search_engine_visibility || false,
          svg_upload_enabled: data.svg_upload_enabled || false,
          disable_feeds: data.disable_feeds || false,
          disable_embeds: data.disable_embeds || false,
          disable_xmlrpc: data.disable_xmlrpc || false,
          revisions_limit: data.revisions_limit !== undefined ? data.revisions_limit : -1,
          redirect_404_to_homepage: data.redirect_404_to_homepage || false,
          maintenance_mode: data.maintenance_mode || false,
          coming_soon_mode: data.coming_soon_mode || false,
          coming_soon_date: data.coming_soon_date || '',
          login_branding_enabled: data.login_branding_enabled || false,
          custom_login_url: data.custom_login_url || '',
          form_submissions_enabled: data.form_submissions_enabled || false,
          form_submissions_plugin: data.form_submissions_plugin || 'fluent_forms',
        });
      }
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load site settings.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // Load feedback token state
    getFeedbackToken(site.id)
      .then(tkn => {
        setFeedbackToken(tkn);
        if (tkn) {
          setLocalAllowGuests(tkn.allow_guests);
          if (tkn.widget_position) {
            const parts = tkn.widget_position.split(',');
            setLocalWidgetPosition(parts[0]);
            if (parts[1]) setLocalWidgetTheme(parts[1]);
            if (parts[2]) setLocalWidgetAccentColor(parts[2]);
          }
        }
      })
      .catch(console.error);
  }, [site.id]);

  const handleToggle = (key: keyof typeof settings, value: boolean | number | string) => {
    setSettings((prev: typeof settings) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const data = await callSiteApi(
        site.url,
        site.publicKey,
        site.secretKey,
        'settings',
        'PUT',
        JSON.stringify(settings)
      );

      if (data?.success) {
        toast({
          title: 'Settings Saved',
          description: 'Your site settings have been updated successfully.',
        });
      } else {
        throw new Error(data?.message || 'Update failed');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save settings.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleFeedbackToggle = async (enable: boolean) => {
    setIsFeedbackLoading(true);
    try {
      if (enable) {
        const tkn = await activateFeedback(site);
        setFeedbackToken(tkn);
        setLocalAllowGuests(tkn.allow_guests);
        if (tkn.widget_position) {
          const parts = tkn.widget_position.split(',');
          setLocalWidgetPosition(parts[0]);
          if (parts[1]) setLocalWidgetTheme(parts[1]);
          if (parts[2]) setLocalWidgetAccentColor(parts[2]);
        }
        toast({ title: 'Feedback Widget Enabled' });
      } else {
        await deactivateFeedback(site);
        setFeedbackToken(prev => prev ? { ...prev, is_active: false } : null);
        toast({ title: 'Feedback Widget Disabled' });
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsFeedbackLoading(false);
    }
  };

  const handleGuestToggle = async (allow: boolean) => {
    setLocalAllowGuests(allow);
    try {
      await updateFeedbackSettings(site, allow, localWidgetPosition, localWidgetTheme, localWidgetAccentColor);
      setFeedbackToken(prev => prev ? { ...prev, allow_guests: allow } : null);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handlePositionChange = async (position: string) => {
    setLocalWidgetPosition(position);
    try {
      await updateFeedbackSettings(site, localAllowGuests, position, localWidgetTheme, localWidgetAccentColor);
      setFeedbackToken(prev => prev ? { ...prev, widget_position: position } : null);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleThemeChange = async (theme: string) => {
    setLocalWidgetTheme(theme);
    try {
      await updateFeedbackSettings(site, localAllowGuests, localWidgetPosition, theme, localWidgetAccentColor);
      setFeedbackToken(prev => prev ? { ...prev, widget_theme: theme } : null);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleAccentColorChange = async (color: string) => {
    setLocalWidgetAccentColor(color);
    try {
      await updateFeedbackSettings(site, localAllowGuests, localWidgetPosition, localWidgetTheme, color);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 w-full animate-pulse">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-7 w-48 bg-muted rounded mb-2" />
            <div className="h-4 w-72 bg-muted rounded" />
          </div>
          <div className="h-10 w-32 bg-muted rounded-lg" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-muted flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-48 bg-muted rounded" />
                  <div className="h-4 w-full max-w-[450px] bg-muted rounded" />
                  <div className="h-4 w-3/4 max-w-[300px] bg-muted rounded" />
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-muted flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-48 bg-muted rounded" />
                  <div className="h-4 w-full max-w-[450px] bg-muted rounded" />
                  <div className="h-4 w-3/4 max-w-[300px] bg-muted rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">Site Settings</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage global configuration for your WordPress site.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Important Settings */}
        <div className="space-y-4">
          {/* Search Engine Visibility Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${!settings.search_engine_visibility ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}>
              <Globe className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="search-engine-visibility" className="text-base font-bold text-foreground cursor-pointer">
                    Search Engine Visibility
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    Allow search engines to index this site. Recommended for live sites to ensure visibility.
                  </p>
                </div>
                <Switch
                  id="search-engine-visibility"
                  checked={settings.search_engine_visibility}
                  onCheckedChange={(checked) => handleToggle('search_engine_visibility', checked)}
                />
              </div>
            </div>
          </div>

          {/* New User Registration Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${!settings.disable_new_user_registration ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}>
              {!settings.disable_new_user_registration ? <UserPlus className="w-5 h-5" /> : <UserX className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="registration" className="text-base font-bold text-foreground cursor-pointer">
                    New User Registration
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    Allow new visitors to register. Disabling this is recommended for security unless required.
                  </p>
                </div>
                <Switch
                  id="registration"
                  checked={!settings.disable_new_user_registration}
                  onCheckedChange={(checked) => handleToggle('disable_new_user_registration', !checked)}
                />
              </div>
            </div>
          </div>

          {/* Login/Signup Branding Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${!settings.login_branding_enabled ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}>
              <ImageIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="login-branding" className="text-base font-bold text-foreground cursor-pointer">
                    Login/Signup Branding
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    Use your site's icon instead of the WordPress logo on the login, signup, and password reset pages.
                  </p>
                </div>
                <Switch
                  id="login-branding"
                  checked={settings.login_branding_enabled}
                  onCheckedChange={(checked) => handleToggle('login_branding_enabled', checked)}
                />
              </div>
            </div>
          </div>

          {/* Custom Login URL Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${!settings.custom_login_url ? 'bg-zinc-500/10 text-zinc-600 border-zinc-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}>
              <Lock className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-col gap-3">
                <div>
                  <Label htmlFor="custom-login-url" className="text-base font-bold text-foreground">
                    Custom Login URL
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    Change the default wp-admin login URL to something more secure (e.g., "portal"). Leave empty to use default.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">{site.url.replace(/\/$/, '')}/</span>
                  <Input
                    id="custom-login-url"
                    value={settings.custom_login_url}
                    onChange={(e) => handleToggle('custom_login_url', e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase())}
                    placeholder="wp-admin"
                    className="max-w-[200px]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Submissions Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${settings.form_submissions_enabled ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' : 'bg-zinc-500/10 text-zinc-600 border-zinc-500/20'}`}>
              <Database className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="form-submissions" className="text-base font-bold text-foreground cursor-pointer">
                    Form Submissions
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    Fetch and display form submissions from supported plugins (Fluent Forms, Gravity Forms, WPForms, Contact Form 7, and more).
                  </p>
                </div>
                <Switch
                  id="form-submissions"
                  checked={settings.form_submissions_enabled}
                  onCheckedChange={(checked) => handleToggle('form_submissions_enabled', checked)}
                />
              </div>
              
              {settings.form_submissions_enabled && (
                <div className="mt-4 pt-4 border-t border-border/50 animate-in fade-in slide-in-from-top-2">
                  <Label htmlFor="form-plugin" className="text-sm font-semibold mb-2 block text-foreground">
                    Active Form Plugin
                  </Label>
                  <Select
                    value={settings.form_submissions_plugin}
                    onValueChange={(value) => handleToggle('form_submissions_plugin', value)}
                  >
                    <SelectTrigger id="form-plugin" className="w-full">
                      <SelectValue placeholder="Select form plugin..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fluent_forms">Fluent Forms</SelectItem>
                      <SelectItem value="gravity_forms">Gravity Forms</SelectItem>
                      <SelectItem value="wpforms">WPForms</SelectItem>
                      <SelectItem value="contact_form_7">Contact Form 7</SelectItem>
                      <SelectItem value="bit_forms">Bit Forms</SelectItem>
                      <SelectItem value="ws_forms">WS Forms</SelectItem>
                      <SelectItem value="bricks_forms">Bricks Native Form</SelectItem>
                      <SelectItem value="bricksforge_pro">Bricksforge Pro Forms</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* XML-RPC Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${!settings.disable_xmlrpc ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}>
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="xml-rpc" className="text-base font-bold text-foreground cursor-pointer">
                    XML-RPC Protocol
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    Legacy remote publishing API. Disabling it is highly recommended to prevent brute force attacks.
                  </p>
                </div>
                <Switch
                  id="xml-rpc"
                  checked={!settings.disable_xmlrpc}
                  onCheckedChange={(checked) => handleToggle('disable_xmlrpc', !checked)}
                />
              </div>
            </div>
          </div>

          {/* WP Comments Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${!settings.disable_comments ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}>
              <MessageSquare className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="comments" className="text-base font-bold text-foreground cursor-pointer">
                    WP Comments
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    Allow visitors to post comments. Disabling this is recommended to eliminate spam and bloat.
                  </p>
                </div>
                <Switch
                  id="comments"
                  checked={!settings.disable_comments}
                  onCheckedChange={(checked) => handleToggle('disable_comments', !checked)}
                />
              </div>
            </div>
          </div>

          {/* 404 Redirect Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${settings.redirect_404_to_homepage ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}>
              <Route className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="redirect-404" className="text-base font-bold text-foreground cursor-pointer">
                    404 Redirect to Homepage
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    Redirect all 404s to home. Not recommended for long-term SEO; use specific redirects instead.
                  </p>
                </div>
                <Switch
                  id="redirect-404"
                  checked={settings.redirect_404_to_homepage}
                  onCheckedChange={(checked) => handleToggle('redirect_404_to_homepage', checked)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Other Settings */}
        <div className="space-y-4">
          {/* ── Feedback Widget Setting ── */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${
              feedbackToken?.is_active
                ? 'bg-red-500/10 text-red-600 border-red-500/20'
                : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
            }`}>
              <MessageSquareMore className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="feedback-widget" className="text-base font-bold text-foreground cursor-pointer">
                    Feedback Widget
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    Show a floating pin-drop feedback tool on your site. Manage submissions in the Entries → Feedback tab.
                  </p>
                </div>
                {isFeedbackLoading
                  ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground flex-shrink-0" />
                  : <Switch
                      id="feedback-widget"
                      checked={feedbackToken?.is_active === true}
                      onCheckedChange={handleFeedbackToggle}
                    />
                }
              </div>
              {feedbackToken?.is_active && (
                <div className="mt-6 pt-6 border-t border-border/50 grid grid-cols-3 gap-8 items-start">
                  
                  {/* Right Column: Widget Position */}
                  <div className="order-3">
                    <Label className="text-sm font-semibold text-foreground mb-4 block">Widget Position</Label>
                    <div className="flex justify-start mt-6 ml-4">
                      <div className="relative w-36 h-20 border-2 border-dashed border-border rounded-xl bg-muted/10">
                        <div className="absolute inset-0 flex items-center justify-center opacity-40 text-xs font-semibold text-muted-foreground pointer-events-none">
                          Screen Preview
                        </div>
                        
                        {/* Top Left */}
                        <button
                          onClick={() => handlePositionChange('top-left')}
                          className={`absolute -top-4 -left-4 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${localWidgetPosition === 'top-left' ? 'text-white z-10' : 'bg-background border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'}`}
                          style={localWidgetPosition === 'top-left' ? { backgroundColor: defaultWorkspaceAccent, borderColor: defaultWorkspaceAccent } : undefined}
                          title="Top Left"
                        >
                          {localWidgetPosition === 'top-left' ? <MessageSquare className="w-4 h-4" /> : <ArrowUpLeft className="w-4 h-4" />}
                        </button>
                        
                        {/* Top Center */}
                        <button
                          onClick={() => handlePositionChange('top-center')}
                          className={`absolute -top-4 left-[calc(50%-16px)] w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${localWidgetPosition === 'top-center' ? 'text-white z-10' : 'bg-background border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'}`}
                          style={localWidgetPosition === 'top-center' ? { backgroundColor: defaultWorkspaceAccent, borderColor: defaultWorkspaceAccent } : undefined}
                          title="Top Center"
                        >
                          {localWidgetPosition === 'top-center' ? <MessageSquare className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
                        </button>
                        
                        {/* Top Right */}
                        <button
                          onClick={() => handlePositionChange('top-right')}
                          className={`absolute -top-4 -right-4 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${localWidgetPosition === 'top-right' ? 'text-white z-10' : 'bg-background border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'}`}
                          style={localWidgetPosition === 'top-right' ? { backgroundColor: defaultWorkspaceAccent, borderColor: defaultWorkspaceAccent } : undefined}
                          title="Top Right"
                        >
                          {localWidgetPosition === 'top-right' ? <MessageSquare className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                        </button>
                        
                        {/* Left Center */}
                        <button
                          onClick={() => handlePositionChange('left-center')}
                          className={`absolute top-[calc(50%-16px)] -left-4 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${localWidgetPosition === 'left-center' ? 'text-white z-10' : 'bg-background border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'}`}
                          style={localWidgetPosition === 'left-center' ? { backgroundColor: defaultWorkspaceAccent, borderColor: defaultWorkspaceAccent } : undefined}
                          title="Left Center"
                        >
                          {localWidgetPosition === 'left-center' ? <MessageSquare className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
                        </button>
                        
                        {/* Right Center */}
                        <button
                          onClick={() => handlePositionChange('right-center')}
                          className={`absolute top-[calc(50%-16px)] -right-4 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${localWidgetPosition === 'right-center' ? 'text-white z-10' : 'bg-background border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'}`}
                          style={localWidgetPosition === 'right-center' ? { backgroundColor: defaultWorkspaceAccent, borderColor: defaultWorkspaceAccent } : undefined}
                          title="Right Center"
                        >
                          {localWidgetPosition === 'right-center' ? <MessageSquare className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                        </button>
                        
                        {/* Bottom Left */}
                        <button
                          onClick={() => handlePositionChange('bottom-left')}
                          className={`absolute -bottom-4 -left-4 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${localWidgetPosition === 'bottom-left' ? 'text-white z-10' : 'bg-background border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'}`}
                          style={localWidgetPosition === 'bottom-left' ? { backgroundColor: defaultWorkspaceAccent, borderColor: defaultWorkspaceAccent } : undefined}
                          title="Bottom Left"
                        >
                          {localWidgetPosition === 'bottom-left' ? <MessageSquare className="w-4 h-4" /> : <ArrowDownLeft className="w-4 h-4" />}
                        </button>
                        
                        {/* Bottom Center */}
                        <button
                          onClick={() => handlePositionChange('bottom-center')}
                          className={`absolute -bottom-4 left-[calc(50%-16px)] w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${localWidgetPosition === 'bottom-center' ? 'text-white z-10' : 'bg-background border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'}`}
                          style={localWidgetPosition === 'bottom-center' ? { backgroundColor: defaultWorkspaceAccent, borderColor: defaultWorkspaceAccent } : undefined}
                          title="Bottom Center"
                        >
                          {localWidgetPosition === 'bottom-center' ? <MessageSquare className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                        </button>
                        
                        {/* Bottom Right */}
                        <button
                          onClick={() => handlePositionChange('bottom-right')}
                          className={`absolute -bottom-4 -right-4 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${localWidgetPosition === 'bottom-right' ? 'text-white z-10' : 'bg-background border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'}`}
                          style={localWidgetPosition === 'bottom-right' ? { backgroundColor: defaultWorkspaceAccent, borderColor: defaultWorkspaceAccent } : undefined}
                          title="Bottom Right"
                        >
                          {localWidgetPosition === 'bottom-right' ? <MessageSquare className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Middle Column: Widget Theme */}
                  <div className="order-2">
                    <Label className="text-sm font-semibold text-foreground mb-4 block">Widget Theme</Label>
                    
                    <div className="mb-3">
                      <div className="flex items-center p-1 border border-border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors focus-within:ring-1 focus-within:ring-border focus-within:border-border focus-within:bg-background">
                        <Popover open={isColorPickerOpen} onOpenChange={setIsColorPickerOpen}>
                          <PopoverTrigger asChild>
                            <div
                              className="w-8 h-8 rounded-md border border-border/50 cursor-pointer shadow-sm ml-1"
                              style={{ backgroundColor: localWidgetAccentColor || defaultWorkspaceAccent }}
                            />
                          </PopoverTrigger>
                          <PopoverContent className="w-80 p-4 bg-popover border-border">
                            <HexColorPicker
                              color={localWidgetAccentColor || defaultWorkspaceAccent}
                              onChange={(newColor) => handleAccentColorChange(newColor.toUpperCase())}
                              style={{ width: '100%' }}
                            />
                            <div className="mt-3">
                              <Input
                                value={localWidgetAccentColor || defaultWorkspaceAccent}
                                onChange={(e) => handleAccentColorChange(e.target.value.toUpperCase())}
                                className="font-mono text-sm bg-background border-input text-foreground"
                                placeholder={defaultWorkspaceAccent}
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Input
                          value={localWidgetAccentColor}
                          onChange={(e) => handleAccentColorChange(e.target.value.toUpperCase())}
                          className="font-mono text-sm flex-1 bg-transparent border-0 focus-visible:ring-0 shadow-none px-3 text-foreground font-medium"
                          placeholder={defaultWorkspaceAccent}
                        />
                        <button
                          onClick={() => handleAccentColorChange('')}
                          className="p-1.5 mr-1 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors flex-shrink-0"
                          title="Reset to Workspace Accent Color"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center bg-muted/50 p-1 rounded-lg border border-border">
                      <button
                        onClick={() => handleThemeChange('dark')}
                        className={`flex-1 py-1.5 px-3 rounded-md text-sm transition-all flex items-center justify-center gap-2 ${
                          localWidgetTheme === 'dark'
                            ? 'bg-background shadow-md ring-1 ring-border/50 text-foreground font-semibold'
                            : 'text-muted-foreground hover:text-foreground font-medium'
                        }`}
                      >
                        <Moon className="w-4 h-4" />
                        Dark
                      </button>
                      <button
                        onClick={() => handleThemeChange('light')}
                        className={`flex-1 py-1.5 px-3 rounded-md text-sm transition-all flex items-center justify-center gap-2 ${
                          localWidgetTheme === 'light'
                            ? 'bg-background shadow-md ring-1 ring-border/50 text-foreground font-semibold'
                            : 'text-muted-foreground hover:text-foreground font-medium'
                        }`}
                      >
                        <Sun className="w-4 h-4" />
                        Light
                      </button>
                    </div>
                  </div>

                  {/* Left Column: Who can submit feedback */}
                  <div className="order-1">
                    <Label className="text-sm font-semibold text-foreground mb-4 block">Who can submit feedback?</Label>
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={() => handleGuestToggle(true)}
                        className={`w-full py-3 px-3 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                          localAllowGuests
                            ? ''
                            : 'border-border bg-background text-muted-foreground hover:bg-muted'
                        }`}
                        style={localAllowGuests ? { borderColor: `${defaultWorkspaceAccent}66`, backgroundColor: `${defaultWorkspaceAccent}1A`, color: defaultWorkspaceAccent } : undefined}
                      >
                        <Globe className="w-4 h-4" />
                        All Visitors
                      </button>
                      <button
                        onClick={() => handleGuestToggle(false)}
                        className={`w-full py-3 px-3 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                          !localAllowGuests
                            ? ''
                            : 'border-border bg-background text-muted-foreground hover:bg-muted'
                        }`}
                        style={!localAllowGuests ? { borderColor: `${defaultWorkspaceAccent}66`, backgroundColor: `${defaultWorkspaceAccent}1A`, color: defaultWorkspaceAccent } : undefined}
                      >
                        <Lock className="w-4 h-4" />
                        Logged-in Only
                      </button>
                    </div>
                  </div>
                  
                </div>
              )}
            </div>
          </div>
          {/* Offline Mode Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex flex-col gap-4">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-xl border flex-shrink-0 ${settings.maintenance_mode || settings.coming_soon_mode ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}>
                <Hammer className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor="maintenance-mode" className="text-base font-bold text-foreground cursor-pointer">
                      Offline Mode
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                      Put your site in an offline state (Maintenance or Coming Soon). Visitors will see a professional landing page.
                    </p>
                  </div>
                  <Switch
                    id="maintenance-mode"
                    checked={settings.maintenance_mode || settings.coming_soon_mode}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        handleToggle('maintenance_mode', true);
                        handleToggle('coming_soon_mode', false);
                      } else {
                        handleToggle('maintenance_mode', false);
                        handleToggle('coming_soon_mode', false);
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {(settings.maintenance_mode || settings.coming_soon_mode) && (
              <div className="pt-4 border-t border-border/50 animate-in fade-in slide-in-from-top-2">
                <Label className="text-sm font-semibold mb-3 block text-foreground">Offline Mode Type</Label>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div
                    className={`border rounded-lg p-3 cursor-pointer flex flex-col items-center justify-center gap-2 transition-all ${
                      settings.maintenance_mode && !settings.coming_soon_mode
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border bg-transparent hover:bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => {
                      handleToggle('maintenance_mode', true);
                      handleToggle('coming_soon_mode', false);
                    }}
                  >
                    <Hammer className="w-5 h-5" />
                    <span className="text-sm font-medium">Maintenance</span>
                  </div>
                  <div
                    className={`border rounded-lg p-3 cursor-pointer flex flex-col items-center justify-center gap-2 transition-all ${
                      settings.coming_soon_mode
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border bg-transparent hover:bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => {
                      handleToggle('maintenance_mode', false);
                      handleToggle('coming_soon_mode', true);
                    }}
                  >
                    <div className="text-xl leading-none">🚀</div>
                    <span className="text-sm font-medium">Coming Soon</span>
                  </div>
                </div>

                {settings.coming_soon_mode && (
                  <div className="space-y-3 animate-in fade-in slide-in-from-top-1">
                    <div>
                      <Label htmlFor="coming-soon-date" className="text-sm font-semibold text-foreground">Launch Date & Time (Optional)</Label>
                      <p className="text-xs text-muted-foreground mt-1 mb-2">
                        Set a launch date to display a countdown timer. Leave empty to hide the timer.
                      </p>
                    </div>
                    <Input
                      id="coming-soon-date"
                      type="datetime-local"
                      value={settings.coming_soon_date || ''}
                      onChange={(e) => handleToggle('coming_soon_date', e.target.value)}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Revisions Limit Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${settings.revisions_limit === -1 ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}>
              <History className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <Label htmlFor="revisions-limit" className="text-base font-bold text-foreground cursor-pointer">
                    Post Revisions Limit
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    A limit of 10-20 is recommended to prevent database bloat and slow site performance. Use -1 for unlimited and 0 to disable revisions.
                  </p>
                </div>
                <div className="w-24">
                  <Input
                    id="revisions-limit"
                    type="number"
                    min="-1"
                    value={settings.revisions_limit}
                    onChange={(e) => handleToggle('revisions_limit', parseInt(e.target.value, 10) || -1)}
                    className="font-mono text-center"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SVG Upload Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${settings.svg_upload_enabled ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}>
              <ImageIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="svg-upload-enabled" className="text-base font-bold text-foreground cursor-pointer">
                    SVG File Uploads
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    Allow uploading SVGs. Disabling this is recommended as SVGs can be used for XSS attacks.
                  </p>
                </div>
                <Switch
                  id="svg-upload-enabled"
                  checked={settings.svg_upload_enabled}
                  onCheckedChange={(checked) => handleToggle('svg_upload_enabled', checked)}
                />
              </div>
            </div>
          </div>

          {/* RSS Feeds Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${settings.disable_feeds ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}>
              <Rss className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="feeds" className="text-base font-bold text-foreground cursor-pointer">
                    RSS & RSS2 Feeds
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    Generate feeds for automatic content distribution. Recommended for SEO and reach.
                  </p>
                </div>
                <Switch
                  id="feeds"
                  checked={!settings.disable_feeds}
                  onCheckedChange={(checked) => handleToggle('disable_feeds', !checked)}
                />
              </div>
            </div>
          </div>

          {/* oEmbeds Setting */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex items-start gap-4">
            <div className={`p-3 rounded-xl border flex-shrink-0 ${settings.disable_embeds ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}>
              <Code className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="embeds" className="text-base font-bold text-foreground cursor-pointer">
                    Native oEmbed Support
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[450px]">
                    Enables automatic rich media embedding. Recommended for a better content experience.
                  </p>
                </div>
                <Switch
                  id="embeds"
                  checked={!settings.disable_embeds}
                  onCheckedChange={(checked) => handleToggle('disable_embeds', !checked)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
